// ai-runtime.ts — shared AI client factory + access-token helper.
// Centralises the Supabase-session token logic so every feature (2.1+) can
// call createAiClient() without knowing how the token is obtained.
// Previously this logic lived inline inside ai-settings-section.tsx.

import { createProxyClient } from "./proxy-provider";
import { getProxyUrl } from "./ai-config";
import type { AiClient } from "./ai-client";

/**
 * Retrieve the current Supabase access token from the persisted session.
 * Uses a dynamic import of @supabase/supabase-js with the same env vars as the
 * app singleton so the persisted localStorage session is picked up automatically.
 * Returns null when the user is signed out or env vars are missing.
 */
export async function getAccessToken(): Promise<string | null> {
  try {
    const { createClient } = await import("@supabase/supabase-js");
    const url = import.meta.env.VITE_SUPABASE_URL as string | undefined;
    const anon = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;
    if (!url || !anon) return null;
    // persistSession:true reads the existing token from localStorage.
    // autoRefreshToken:false avoids kicking off background refresh here.
    const sb = createClient(url, anon, {
      auth: { persistSession: true, autoRefreshToken: false },
    });
    const { data } = await sb.auth.getSession();
    return data.session?.access_token ?? null;
  } catch {
    return null;
  }
}

/**
 * Create a ready-to-use AiClient wired to the Supabase Edge Function proxy.
 * Call this in any Phase 2.x feature that needs to talk to the AI.
 * The Gemini API key never touches the browser — the proxy holds it.
 */
export function createAiClient(): AiClient {
  return createProxyClient(getAccessToken, getProxyUrl);
}
